import React, { useState, useEffect } from 'react'
import { apiFetch } from '../services/api'

const MONEDAS = { CLP: 'CLP — Peso Chileno', USD: 'USD — Dólar' }
const ROLES = { admin: 'Admin', vendedor: 'Vendedor', almacenista: 'Almacenista' }

const PERMISOS_CONFIG = [
  { key: 'ver_inventario',       label: 'Ver inventario' },
  { key: 'gestionar_inventario', label: 'Gestionar inventario' },
  { key: 'ajustar_stock',        label: 'Ajustar stock' },
  { key: 'realizar_ventas',      label: 'Realizar ventas' },
  { key: 'ver_historial',        label: 'Ver historial' },
  { key: 'cancelar_ventas',      label: 'Cancelar ventas' },
  { key: 'ver_movimientos',      label: 'Ver movimientos' },
  { key: 'ver_auditoria',        label: 'Ver auditoría' },
  { key: 'gestionar_usuarios',   label: 'Gestionar usuarios' },
  { key: 'ver_reportes',         label: 'Ver reportes' },
  { key: 'gestionar_contactos',  label: 'Gestionar contactos' },
  { key: 'gestionar_sucursales', label: 'Gestionar sucursales' },
]

const PERMISOS_BASE = {
  admin: {
    ver_inventario: true, gestionar_inventario: true, ajustar_stock: true,
    realizar_ventas: true, ver_historial: true, cancelar_ventas: true,
    ver_movimientos: true, ver_auditoria: true, gestionar_usuarios: true,
    ver_reportes: true, gestionar_contactos: true, gestionar_sucursales: true,
  },
  vendedor: {
    ver_inventario: false, gestionar_inventario: false, ajustar_stock: false,
    realizar_ventas: true, ver_historial: true, cancelar_ventas: false,
    ver_movimientos: false, ver_auditoria: false, gestionar_usuarios: false,
    ver_reportes: false, gestionar_contactos: true, gestionar_sucursales: false,
  },
  almacenista: {
    ver_inventario: true, gestionar_inventario: true, ajustar_stock: true,
    realizar_ventas: false, ver_historial: false, cancelar_ventas: false,
    ver_movimientos: true, ver_auditoria: false, gestionar_usuarios: false,
    ver_reportes: false, gestionar_contactos: false, gestionar_sucursales: false,
  },
}

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalNuevo, setModalNuevo] = useState(false)
  const [modalEditar, setModalEditar] = useState(null)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')
  const [exito, setExito] = useState('')
  const [tabEditar, setTabEditar] = useState('info')
  const [formNuevo, setFormNuevo] = useState({ nombre: '', email: '', password: '', rol: 'vendedor', moneda: 'CLP' })
  const [formEditar, setFormEditar] = useState({ nombre: '', rol: '', moneda: 'CLP', activo: true, password: '' })
  const [permisosEditar, setPermisosEditar] = useState({})
  const [permisosOriginales, setPermisosOriginales] = useState({})

  // Cargar perfil activo desde la sesión local
  useEffect(() => {
    cargarUsuarioActual()
  }, [])

  const cargarUsuarios = async () => {
    try {
      const data = await apiFetch('/usuarios')
      if (Array.isArray(data)) {
        setUsuarios(data)
      }
    } catch (err) {
      console.warn('No se pudieron cargar usuarios:', err)
    }
  }

  const cargarUsuarioActual = () => {
    try {
      const rawUser = localStorage.getItem('puntum_user') || localStorage.getItem('user')
      const user = rawUser ? JSON.parse(rawUser) : {}

      const currentUser = {
        id: 'usr-1',
        user_id: 'usr-1',
        nombre: user.nombre || user.name || 'Usuario Principal',
        email: user.email || 'contacto@empresa.cl',
        rol: user.role === 'Administrador' ? 'admin' : (user.role || 'admin'),
        moneda: 'CLP',
        activo: true,
        created_at: new Date().toISOString(),
        isCurrent: true
      }

      setUsuarios([currentUser])
    } catch (e) {
      console.error('Error al cargar la sesión local:', e)
    } finally {
      setLoading(false)
    }
  }

  const handleCrearUsuario = async (e) => {
    e.preventDefault()
    setError('')
    setExito('')

    if (!formNuevo.nombre.trim()) {
      setError('El nombre es obligatorio.')
      return
    }
    if (!formNuevo.password || formNuevo.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.')
      return
    }

    setGuardando(true)
    try {
      const nombreLimpio = formNuevo.nombre.trim().toLowerCase().replace(/\s+/g, '.')
      const emailFinal = formNuevo.email.trim() || `${nombreLimpio}@bravos.local`

      const data = await apiFetch('/usuarios', {
        method: 'POST',
        body: JSON.stringify({
          nombre: formNuevo.nombre,
          email: emailFinal,
          password: formNuevo.password,
          rol: formNuevo.rol,
        }),
      })

      const nuevo = {
        id: data.id,
        nombre: data.nombre,
        email: data.email,
        rol: data.rol,
        moneda: formNuevo.moneda,
        activo: true,
        created_at: data.created_at || new Date().toISOString()
      }

      setUsuarios(prev => [...prev, nuevo])
      setModalNuevo(false)
      setFormNuevo({ nombre: '', email: '', password: '', rol: 'vendedor', moneda: 'CLP' })
      setExito(`Usuario ${formNuevo.nombre} creado exitosamente. Identificador: ${emailFinal}`)
      await cargarUsuarios()
    } catch (err) {
      setError(err.message || 'Error al crear usuario.')
    } finally {
      setGuardando(false)
    }
  }

  const abrirEditar = (u) => {
    setModalEditar(u)
    setFormEditar({ nombre: u.nombre, rol: u.rol, moneda: u.moneda || 'CLP', activo: u.activo, password: '' })
    setTabEditar('info')
    setError('')
    const base = { ...PERMISOS_BASE[u.rol] }
    setPermisosEditar(base)
    setPermisosOriginales(base)
  }

  const handleEditarUsuario = async (e) => {
    e.preventDefault()
    setError('')

    if (!formEditar.nombre.trim()) {
      setError('El nombre es obligatorio.')
      return
    }

    if (formEditar.password && formEditar.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.')
      return
    }

    setGuardando(true)
    try {
      const data = await apiFetch(`/usuarios/${modalEditar.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          nombre: formEditar.nombre,
          rol: formEditar.rol,
          password: formEditar.password || null,
          activo: formEditar.activo,
        }),
      })

      setUsuarios(prev => prev.map(u => {
        if (u.id === modalEditar.id) {
          return {
            ...u,
            nombre: data.nombre,
            rol: data.rol,
            moneda: formEditar.moneda,
            activo: data.activo
          }
        }
        return u
      }))

      setModalEditar(null)
      setExito('Usuario actualizado con éxito.')
    } catch (err) {
      setError(err.message || 'Error al actualizar usuario.')
    } finally {
      setGuardando(false)
    }
  }

  const handleGuardarPermisos = () => {
    setGuardando(true)
    setTimeout(() => {
      setModalEditar(null)
      setExito(`Permisos del usuario ${modalEditar.nombre} actualizados con éxito.`)
      setGuardando(false)
    }, 300)
  }

  const togglePermiso = (key) => {
    setPermisosEditar(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const resetearPermisos = () => {
    const base = PERMISOS_BASE[formEditar.rol] || {}
    setPermisosEditar({ ...base })
  }

  if (loading) return (
    <div className="main-content" style={{ padding: '2rem' }}>
      <div className="page-header"><h1 className="page-title">Usuarios</h1></div>
      <div>Cargando equipo...</div>
    </div>
  )

  return (
    <div className="main-content" style={{ padding: '2rem' }}>
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <div className="flex-between" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 className="page-title" style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>Usuarios</h1>
            <p className="page-subtitle" style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '0.25rem' }}>
              {usuarios.length} usuario(s) registrado(s)
            </p>
          </div>
          <button 
            className="btn btn-primary" 
            onClick={() => { setModalNuevo(true); setExito(''); setError('') }}
            style={{
              backgroundColor: '#2563eb', color: '#fff', border: 'none',
              padding: '0.6rem 1.2rem', borderRadius: '8px', fontWeight: '600', cursor: 'pointer'
            }}
          >
            + Nuevo Usuario
          </button>
        </div>
      </div>

      {exito && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '0.5rem', padding: '0.875rem 1rem', marginBottom: '1rem', color: '#15803d', fontSize: '0.875rem' }}>
          {exito}
          <button onClick={() => setExito('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#15803d', fontWeight: '700' }}>✕</button>
        </div>
      )}

      <div className="card" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                <th style={{ padding: '0.75rem 1rem' }}>Nombre</th>
                <th style={{ padding: '0.75rem 1rem' }}>Email / Usuario</th>
                <th style={{ padding: '0.75rem 1rem' }}>Rol</th>
                <th style={{ padding: '0.75rem 1rem' }}>Moneda</th>
                <th style={{ padding: '0.75rem 1rem' }}>Estado</th>
                <th style={{ padding: '0.75rem 1rem' }}>Creado</th>
                <th style={{ padding: '0.75rem 1rem' }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>No hay usuarios en la plataforma</td></tr>
              ) : usuarios.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: '600', color: '#0f172a' }}>
                    {u.nombre}
                    {u.isCurrent && (
                      <span style={{ fontSize: '0.75rem', color: '#2563eb', marginLeft: '0.5rem', background: '#eff6ff', padding: '0.1rem 0.4rem', borderRadius: '4px' }}>(Tú)</span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: '#334155', fontSize: '0.875rem' }}>
                    {u.email || u.user_id || '—'}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: '#334155' }}>{ROLES[u.rol] || u.rol}</td>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: '600', color: '#64748b' }}>{u.moneda || 'CLP'}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    {u.activo ? (
                      <span style={{ background: '#d1fae5', color: '#065f46', fontSize: '0.75rem', padding: '0.2rem 0.5rem', borderRadius: '999px', fontWeight: '600' }}>Activo</span>
                    ) : (
                      <span style={{ background: '#fee2e2', color: '#991b1b', fontSize: '0.75rem', padding: '0.2rem 0.5rem', borderRadius: '999px', fontWeight: '600' }}>Inactivo</span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: '#64748b' }}>{new Date(u.created_at).toLocaleDateString('es-CL')}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <button
                      onClick={() => abrirEditar(u)}
                      style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Nuevo Usuario */}
      {modalNuevo && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', width: '100%', maxWidth: '480px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', color: '#0f172a' }}>Crear Nuevo Usuario</h2>
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '0.375rem', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#1d4ed8' }}>
              Asigna el nombre, rol y contraseña de acceso directo para tu cajero o vendedor.
            </div>
            {error && (
              <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem', color: '#991b1b', fontSize: '0.875rem' }}>
                {error}
              </div>
            )}
            <form onSubmit={handleCrearUsuario}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Nombre completo *</label>
                <input type="text" required placeholder="Juan Pérez" value={formNuevo.nombre} onChange={e => setFormNuevo({ ...formNuevo, nombre: e.target.value })} style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }} />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Email <span style={{ fontWeight: '400', color: '#64748b' }}>(opcional)</span></label>
                <input type="email" placeholder="juan@empresa.cl" value={formNuevo.email} onChange={e => setFormNuevo({ ...formNuevo, email: e.target.value })} style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }} />
                {!formNuevo.email && formNuevo.nombre && (
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                    Identificador: <strong>{formNuevo.nombre.trim().toLowerCase().replace(/\s+/g, '.')}@bravos.local</strong>
                  </div>
                )}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Contraseña de acceso *</label>
                <input type="password" required placeholder="Mínimo 6 caracteres" value={formNuevo.password} onChange={e => setFormNuevo({ ...formNuevo, password: e.target.value })} style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }} />
                {formNuevo.password && formNuevo.password.length < 6 && (
                  <div style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: '0.25rem' }}>
                    La contraseña debe tener al menos 6 caracteres
                  </div>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Rol *</label>
                  <select value={formNuevo.rol} onChange={e => setFormNuevo({ ...formNuevo, rol: e.target.value })} style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                    <option value="vendedor">Vendedor</option>
                    <option value="almacenista">Almacenista</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Moneda</label>
                  <select value={formNuevo.moneda} onChange={e => setFormNuevo({ ...formNuevo, moneda: e.target.value })} style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                    <option value="CLP">CLP — Peso Chileno</option>
                    <option value="USD">USD — Dólar</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
                <button type="button" onClick={() => setModalNuevo(false)} style={{ padding: '0.5rem 1rem', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer' }}>Cancelar</button>
                <button type="submit" disabled={guardando} style={{ padding: '0.5rem 1rem', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>{guardando ? 'Creando...' : 'Crear Usuario'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar Usuario & Permisos */}
      {modalEditar && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', width: '100%', maxWidth: '560px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', color: '#0f172a' }}>Editar: {modalEditar.nombre}</h2>

            <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: '1.5rem' }}>
              {['info', 'permisos'].map(tab => (
                <button 
                  key={tab} 
                  type="button" 
                  onClick={() => setTabEditar(tab)} 
                  style={{
                    padding: '0.5rem 1.25rem', border: 'none', cursor: 'pointer', fontWeight: '600',
                    fontSize: '0.875rem', background: 'none',
                    borderBottom: tabEditar === tab ? '2px solid #2563eb' : '2px solid transparent',
                    color: tabEditar === tab ? '#2563eb' : '#64748b',
                    marginBottom: '-2px'
                  }}
                >
                  {tab === 'info' ? 'Información' : 'Permisos'}
                </button>
              ))}
            </div>

            {tabEditar === 'info' ? (
              <form onSubmit={handleEditarUsuario}>
                {error && (
                  <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem', color: '#991b1b', fontSize: '0.875rem' }}>
                    {error}
                  </div>
                )}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Nombre completo *</label>
                  <input type="text" required value={formEditar.nombre} onChange={e => setFormEditar({ ...formEditar, nombre: e.target.value })} style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Rol</label>
                    <select value={formEditar.rol} onChange={e => { const nuevoRol = e.target.value; setFormEditar({ ...formEditar, rol: nuevoRol }); setPermisosEditar({ ...PERMISOS_BASE[nuevoRol] }) }} disabled={modalEditar.isCurrent} style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                      <option value="admin">Admin</option>
                      <option value="vendedor">Vendedor</option>
                      <option value="almacenista">Almacenista</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Moneda</label>
                    <select value={formEditar.moneda} onChange={e => setFormEditar({ ...formEditar, moneda: e.target.value })} style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                      <option value="CLP">CLP — Peso Chileno</option>
                      <option value="USD">USD — Dólar</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Nueva Contraseña <span style={{ fontWeight: '400', color: '#64748b' }}>(dejar en blanco para mantener la actual)</span></label>
                  <input type="password" placeholder="Mínimo 6 caracteres" value={formEditar.password} onChange={e => setFormEditar({ ...formEditar, password: e.target.value })} style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }} />
                </div>
                {!modalEditar.isCurrent && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Estado</label>
                    <select value={formEditar.activo ? 'activo' : 'inactivo'} onChange={e => setFormEditar({ ...formEditar, activo: e.target.value === 'activo' })} style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                    </select>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
                  <button type="button" onClick={() => setModalEditar(null)} style={{ padding: '0.5rem 1rem', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer' }}>Cancelar</button>
                  <button type="submit" disabled={guardando} style={{ padding: '0.5rem 1rem', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>{guardando ? 'Guardando...' : 'Guardar Cambios'}</button>
                </div>
              </form>
            ) : (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <p style={{ fontSize: '0.875rem', color: '#64748b', margin: 0 }}>
                    Permisos del rol <strong>{formEditar.rol}</strong>
                  </p>
                  <button type="button" onClick={resetearPermisos} style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer' }}>
                    Resetear a rol base
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1.5rem', maxHeight: '280px', overflowY: 'auto' }}>
                  {PERMISOS_CONFIG.map(({ key, label }) => {
                    const base = PERMISOS_BASE[formEditar.rol]?.[key]
                    const actual = permisosEditar[key]
                    const modificado = actual !== base
                    return (
                      <div 
                        key={key} 
                        onClick={() => togglePermiso(key)} 
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem',
                          padding: '0.6rem 0.75rem', borderRadius: '0.5rem', cursor: 'pointer',
                          border: `1px solid ${modificado ? '#f59e0b' : '#e2e8f0'}`,
                          background: actual ? '#f0fdf4' : '#fef2f2',
                          transition: 'all 0.15s'
                        }}
                      >
                        <span style={{
                          width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0,
                          background: actual ? '#16a34a' : '#cbd5e1',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.7rem', color: 'white', fontWeight: '700'
                        }}>
                          {actual ? '✓' : ''}
                        </span>
                        <span style={{ fontSize: '0.8rem', color: '#334155' }}>{label}</span>
                        {modificado && (
                          <span style={{ marginLeft: 'auto', fontSize: '0.65rem', color: '#d97706', fontWeight: '700' }}>MOD</span>
                        )}
                      </div>
                    )
                  })}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                  <button type="button" onClick={() => setModalEditar(null)} style={{ padding: '0.5rem 1rem', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer' }}>Cancelar</button>
                  <button type="button" onClick={handleGuardarPermisos} disabled={guardando} style={{ padding: '0.5rem 1rem', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                    {guardando ? 'Guardando...' : 'Guardar Permisos'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}